import React, { useEffect, useState, useRef } from "react";
import { useLoading } from "../../../contexts/LoadingContext";
import { FilterSectionStyles } from "../../../styled-components/FilterStyles";
import styled from "styled-components";
import LabelNumber from "../../../styled-components/LabelNumber";
import ClubFinancialOverview from "./ClubFinancialOverview";
import ClubRevenueBreakDown from "./ClubRevenueBreakDown";
import IApiResponse from "../../../types/IApiResponse";
import { fetchData } from "../../../services/api";
import apiUrls from "../../../config/apiUrls";
import handleApiResponse from "../../../utils/handleApiResponse";
import { useParams, useLocation } from "react-router-dom";
import { IClubFinancialData, IseasonData } from "../../../types/IClubDetail";
import ClubPlayerTrading from "./ClubPlayerTrading";
import ClubCosts from "./ClubCosts";
import AssetsLiabilities from "./AssetsLiabilities";
import TotalAssetsLiabilities from "./TotalAssetsLiabilities";
import CashCreditDebt from "./CashCreditDebt";
import LoadingSpinner from "../../../ui/LoadingSpinner";
import PlayerWages from "./PlayerWages";
import LineBarChart from "./LineBarChart";
import CustomDropdown from "../../../ui/CustomDropdown";
import { postData } from "../../../services/api";
import { IFinancialPlayerTransfer } from "../../../types/IClubDetail";
import { useTheme } from "../../../contexts/ThemeContext";

export const StyledHeader = styled.div`
  display: flex;
  width: 100%;
  padding-bottom: 16px;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.playerDataBorder};
  .contentText {
    color: ${({ theme }) => theme.playerColor};
  }
  .club-nav-header {
    padding-left: 0;
  }
`;

const ClubFinancial: React.FC<{ team?: string }> = ({ team }) => {
  const { startLoading, stopLoading, loading } = useLoading();
  const params = useParams<{ teamId: string }>();
  const teamId = params.teamId ?? team;
  const [clubfinancialData, setClubFinancialData] = useState<IClubFinancialData>();
  const [playerWagesData, setPlayerWagesData] = useState<IClubFinancialData>();
  const location = useLocation();
  const { theme } = useTheme();
  const [selectedSection, setSelectedSection] = useState<string>("overview");
  const [seasonData, setSeasonData] = useState<any[]>([]);
  const [seasonLabel, setSeasonLabel] = useState("");
  const [seasonId, setSeasonId] = useState<number>();
  const [tranferseasonId, setTranferSeasonId] = useState<number>();
  const [playerTransferData, setPlayerTransferData] = useState<IFinancialPlayerTransfer[]>([]);
  const [activeSection, setActiveSection] = React.useState<
    "overview" | "RevenueTRading" | "Costswages" | "Assetsliabilities" | "creditdebt"
  >("overview");
  const sectionRefs = {
    overview: useRef<HTMLDivElement>(null),
    RevenueTRading: useRef<HTMLDivElement>(null),
    Costswages: useRef<HTMLDivElement>(null),
    Assetsliabilities: useRef<HTMLDivElement>(null),
    creditdebt: useRef<HTMLDivElement>(null),
  };

  useEffect(() => {
    const handleScroll = () => {
      const windscroll = window.scrollY;
      Object.entries(sectionRefs).forEach(([section, ref]) => {
        const element = ref.current;
        if (element) {
          const sectionTop = element.getBoundingClientRect().top + window.scrollY;
          const sectionHeight = element.offsetHeight;
          const header = document.querySelector(".page-header") as HTMLElement | null;
          const tabs = document.querySelector(".tab-section") as HTMLElement | null;

          const headerHeight = header?.offsetHeight || 0;
          const tabsHeight = tabs?.offsetHeight || 0;
          const offset = 3;
          // Detect if the section is currently in view (adjust offset for sticky header height if needed)
          if (windscroll >= sectionTop - headerHeight - tabsHeight - offset && windscroll < sectionTop + sectionHeight - 200) {
            setActiveSection(section as "overview" | "RevenueTRading" | "Costswages" | "Assetsliabilities" | "creditdebt");
          }
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleHeaderClick = (section: "overview" | "RevenueTRading" | "Costswages" | "Assetsliabilities" | "creditdebt") => {
    setActiveSection(section);
    const element = sectionRefs[section].current;
    if (element) {
      const sectionTop = element.getBoundingClientRect().top + window.scrollY;
      const header = document.querySelector(".page-header") as HTMLElement | null;
      const tabs = document.querySelector(".tab-section") as HTMLElement | null;
      const headerHeight = header?.offsetHeight || 0;
      const tabsHeight = tabs?.offsetHeight || 0;
      const offset = 0; // Desired offset from the top
      const scrollPosition = sectionTop - headerHeight - tabsHeight - offset;
      window.scrollTo({ top: scrollPosition, behavior: "smooth" });
    }
  };

  const scrollToHash = () => {
    const { hash } = location;
    if (hash) {
      const element = document.getElementById(hash.substring(1));
      if (element) {
        window.scrollTo({
          top: element.offsetTop,
          behavior: "smooth",
        });
        setSelectedSection(hash.substring(1));
      }
    }
  };

  useEffect(() => {
    const fetchFinancialData = async () => {
      if (seasonId! > 0) {
        try {
          startLoading();
          const requestParameters = {
            TeamId: teamId,
            seasonId: seasonId,
          };
          const [clubFinancialResponse, playerWagesResponse] = await Promise.all([
            postData(apiUrls["GetTeamFinancialData"], requestParameters) as Promise<IApiResponse<IClubFinancialData>>,
            postData(apiUrls["GetTeamFinancialPlayerWagesData"], requestParameters) as Promise<IApiResponse<IClubFinancialData>>,
            fetchFinancialPlayerTransfer(seasonId),
          ]);
          setClubFinancialData(clubFinancialResponse.data);
          setPlayerWagesData(playerWagesResponse.data);
          scrollToHash(); // Scroll to the hash when data is loaded
        } catch (error) {
          console.error("Error fetching financial data:", error);
        } finally {
          stopLoading();
        }
      }
    };

    fetchFinancialData();
  }, [teamId, seasonId]);

  const fetchFinancialPlayerTransfer = async (seasonId: number | undefined) => {
    try {
      startLoading();
      const ApiRequestData = {
        teamId: teamId,
        transfertype: "all",
        seasonId: seasonId,
      };
      const response: IApiResponse<IFinancialPlayerTransfer[]> = await postData(apiUrls.GetTeamFinancialPlayerTransfers, ApiRequestData);
      handleApiResponse(
        response,
        (playerData) => {
          stopLoading();
          setPlayerTransferData(playerData);
        },
        (error) => {
          console.error("Error fetching PlayerCard Data:", error);
        }
      );
    } catch (error) {
      stopLoading();
      console.error("Error fetching player card data:", error);
    }
  };

  useEffect(() => {
    const fetchSeasonData = async () => {
      const response: IApiResponse<IseasonData> = await fetchData(`${apiUrls["GetAllCompetitionSeasonsByTeamId"]}?TeamId=${teamId}`);
      handleApiResponse(
        response,
        (teamsData) => {
          setSeasonData(teamsData.seasonData);
          setSeasonId(teamsData.previousSeasonId);
          const matchedSeason = teamsData.seasonData.find((season) => season.value === teamsData.previousSeasonId);
          if (matchedSeason) {
            setSeasonLabel(matchedSeason.label);
          }
        },
        (error) => {
          console.error("Error fetching club card Data:", error);
        }
      );
    };
    fetchSeasonData();
  }, [teamId]);

  const handleSeasonChange = (seasonId: string) => {
    setSeasonId(Number(seasonId));
    const matchedSeason = seasonData.find((season) => season.value === seasonId);
    if (matchedSeason) {
      setSeasonLabel(matchedSeason.label);
    }
  };

  useEffect(() => {
    if (tranferseasonId) {
      fetchFinancialPlayerTransfer(tranferseasonId);
    }
  }, [tranferseasonId]);

  const handleTransferSeasonChange = (seasonId: string) => {
    setTranferSeasonId(Number(seasonId));
  };

  useEffect(() => {
    scrollToHash();
  }, [location]);;

  return (
    <div className="player-tab-content financial-tab  ">
      <FilterSectionStyles className="filters-section">
        <StyledHeader className="header-content">
          <div className="custom-dropdown">
            <CustomDropdown placeholder="SEASON"  isDisplaySearchInput={false} options={seasonData} onChange={handleSeasonChange} deafaultSelected={seasonLabel} Id={Number(teamId)}/>
          </div>
        </StyledHeader>
        <div className="club-nav">
          <div className="nav-content">
            <ul className="career-navigation">
              <li
                className={`career-nav-item ${activeSection === "overview" ? "active" : ""}`}
                onClick={() => handleHeaderClick("overview")}
              >
                <span className="indicator"></span>
                <LabelNumber style={{ color: activeSection === "overview" ? theme.playerColor : "" }} className="attack-defense-data">
                  {" "}
                  Overview
                </LabelNumber>
              </li>
              <li
                className={`career-nav-item ${activeSection === "RevenueTRading" ? "active" : ""}`}
                onClick={() => handleHeaderClick("RevenueTRading")}
              >
                <span className="indicator"></span>
                <LabelNumber style={{ color: activeSection === "RevenueTRading" ? theme.playerColor : "" }} className="attack-defense-data">
                  {" "}
                  Revenue & TRading{" "}
                </LabelNumber>
              </li>
              <li
                className={`career-nav-item ${activeSection === "Costswages" ? "active" : ""}`}
                onClick={() => handleHeaderClick("Costswages")}
              >
                <span className="indicator"></span>
                <LabelNumber style={{ color: activeSection === "Costswages" ? theme.playerColor : "" }} className="attack-defense-data">
                  Costs & wages{" "}
                </LabelNumber>
              </li>
              <li
                className={`career-nav-item ${activeSection === "Assetsliabilities" ? "active" : ""}`}
                onClick={() => handleHeaderClick("Assetsliabilities")}
              >
                <span className="indicator"></span>
                <LabelNumber
                  style={{ color: activeSection === "Assetsliabilities" ? theme.playerColor : "" }}
                  className="attack-defense-data"
                >
                  Assets & liabilities{" "}
                </LabelNumber>
              </li>
              <li
                className={`career-nav-item ${activeSection === "creditdebt" ? "active" : ""}`}
                onClick={() => handleHeaderClick("creditdebt")}
              >
                <span className="indicator"></span>
                <LabelNumber style={{ color: activeSection === "creditdebt" ? theme.playerColor : "" }} className="attack-defense-data">
                  credit & debt{" "}
                </LabelNumber>
              </li>
            </ul>
          </div>
        </div>
      </FilterSectionStyles>
      <div className="club-results-section tab-content-right">
        <div className="padding-main">
          <div ref={sectionRefs.overview} id="overview" className="header-content-wrapper padding-inner">
            <ClubFinancialOverview data={clubfinancialData?.financialOverview} />
          </div>
          <div ref={sectionRefs.RevenueTRading} id="revenue" className="header-content-wrapper padding-inner">
            <div className="padding-inner">
              <ClubRevenueBreakDown data={clubfinancialData?.revenueBreakdown} />
            </div>
            <div className="padding-inner">
              <ClubPlayerTrading
                data={clubfinancialData?.playerTrading}
                transferData={playerTransferData}
                seasonData={seasonData}
                onChange={handleTransferSeasonChange}
                seasonLabel={seasonLabel}
              />
            </div>
          </div>
        </div>
        <div className="padding-main">
          <div ref={sectionRefs.Costswages} id="costs" className="header-content-wrapper">
            <div className="padding-inner">
              <ClubCosts data={clubfinancialData?.costs} />
            </div>
            <div className="padding-inner wages-padding">
              <PlayerWages data={playerWagesData?.playerWages} />
              <LineBarChart
                xAxisValues={playerWagesData?.wagesOverTime.xAxisValues!}
                yAxisValues={playerWagesData?.wagesOverTime.yAxisValues!}
                clubWages={playerWagesData?.wagesOverTime.leagueAverageTotalWages!}
                leagueWages={playerWagesData?.wagesOverTime.clubAverageTotalWages!}
              />
            </div>
          </div>
        </div>
        <div className="padding-main">
          <div ref={sectionRefs.Assetsliabilities} id="assets" className="header-content-wrapper">
            {/* This graph of Assets & liabilities commented as per WI-1197 by Scott 
              <div className="padding-inner">
                <AssetsLiabilities data={clubfinancialData?.assetsLiabilities} />
              </div> 
            */}
            <div className="padding-inner">
              <TotalAssetsLiabilities data={clubfinancialData?.totalAssets} liabilitiesData={clubfinancialData?.totalLiabilities} />
            </div>
          </div>
        </div>
        <div className="padding-main">
          <div ref={sectionRefs.creditdebt} id="credit" className="header-content-wrapper">
            <CashCreditDebt data={clubfinancialData?.cashCredit!} debtsData={clubfinancialData?.debts!} />
          </div>
        </div>
      </div>
      {loading && <LoadingSpinner />}
    </div>
  );
};

export default ClubFinancial;
